import { useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { RotateCcw, Sparkles } from "lucide-react";

// User's BAYES VBA macro — inputs 0–100 (as percentages), output 0–100.
function bayes(prior, pro, contra) {
  const p = prior / 100;
  const pr = pro / 100;
  const co = contra / 100;
  const a1 = p * pr;
  const a2 = 1 - p;
  const a3 = a2 * co;
  const a4 = a1 + a3;
  if (a4 === 0) return 0;
  return (a1 / a4) * 100;
}

function parseNum(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const fmt = (n, digits = 2) =>
  n === null || n === undefined ? "—" : `${Number(n).toFixed(digits)}`;
const fmtPct = (n, digits = 2) =>
  n === null || n === undefined ? "—" : `${Number(n).toFixed(digits)}%`;

export default function CheckStockPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isGuest = user?.role === "guest";
  const [ticker, setTicker] = useState("");
  const [industry, setIndustry] = useState("");
  const [price, setPrice] = useState("");
  const [marketCap, setMarketCap] = useState("");
  const [dividend, setDividend] = useState("");
  const [pe, setPe] = useState("");
  const [pb, setPb] = useState("");

  const peN = parseNum(pe);
  const pbN = parseNum(pb);
  const priceN = parseNum(price);
  const dividendN = parseNum(dividend);
  const marketCapN = parseNum(marketCap);

  // Steps must all have required inputs. Ticker / industry are descriptive only.
  const canCalc =
    peN !== null &&
    pbN !== null &&
    priceN !== null &&
    priceN !== 0 &&
    dividendN !== null &&
    marketCapN !== null;

  const result = useMemo(() => {
    if (!canCalc) return null;

    // 1) =MAX(1, BAYES(50, 70-PE, 30+PE))
    const step1 = Math.max(1, bayes(50, 70 - peN, 30 + peN));

    // 2) =MAX(1, BAYES(step1, 70-(PB*10), 30+(PB*10)))
    const step2 = Math.max(1, bayes(step1, 70 - pbN * 10, 30 + pbN * 10));

    // 3) =dividend / price
    const step3 = dividendN / priceN;

    // 4) =MIN(99, IF(marketCap < 1, 0, BAYES(step2, 50 + step3*1000, 50 - step3*1000)))
    const step4 =
      marketCapN < 1
        ? 0
        : Math.min(99, bayes(step2, 50 + step3 * 1000, 50 - step3 * 1000));

    return { step1, step2, step3, step4 };
  }, [canCalc, peN, pbN, priceN, dividendN, marketCapN]);

  const reset = () => {
    setTicker("");
    setIndustry("");
    setPrice("");
    setMarketCap("");
    setDividend("");
    setPe("");
    setPb("");
  };

  const finalScore = result?.step4 ?? null;

  return (
    <div className="space-y-8 max-w-6xl" data-testid="check-stock-page">
      <p className="text-slate-600 text-sm leading-relaxed max-w-2xl">
        Enter the stock's fundamentals. The page runs your 4-step Bayes
        procedure and outputs the final probability that it's a "good" stock.
      </p>

      <div className="grid lg:grid-cols-5 gap-6 items-start">
        {/* Inputs */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold tracking-tight text-slate-900 font-heading">
              Stock Fundamentals
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={reset}
              className="gap-2"
              data-testid="reset-button"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="ticker" label="Ticker" testid="ticker-input">
              <Input
                id="ticker"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="AAPL"
                maxLength={10}
                className="mt-1.5 font-mono uppercase"
                data-testid="ticker-input"
              />
            </Field>
            <Field id="industry" label="Industry" testid="industry-input">
              <Input
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Consumer Electronics"
                className="mt-1.5"
                data-testid="industry-input"
              />
            </Field>
            <Field id="price" label="Current Price" suffix="$" testid="price-input">
              <Input
                id="price"
                type="number"
                inputMode="decimal"
                step="any"
                min={0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="180.25"
                className="mt-1.5 font-mono tabular-nums"
                data-testid="price-input"
              />
            </Field>
            <Field id="marketCap" label="Market Cap" suffix="B" testid="market-cap-input">
              <Input
                id="marketCap"
                type="number"
                inputMode="decimal"
                step="any"
                min={0}
                value={marketCap}
                onChange={(e) => setMarketCap(e.target.value)}
                placeholder="2800"
                className="mt-1.5 font-mono tabular-nums"
                data-testid="market-cap-input"
              />
            </Field>
            <Field id="dividend" label="Dividend" suffix="¢" testid="dividend-input">
              <Input
                id="dividend"
                type="number"
                inputMode="decimal"
                step="any"
                min={0}
                value={dividend}
                onChange={(e) => setDividend(e.target.value)}
                placeholder="96"
                className="mt-1.5 font-mono tabular-nums"
                data-testid="dividend-input"
              />
            </Field>
            <Field id="pe" label="P/E Ratio" testid="pe-input">
              <Input
                id="pe"
                type="number"
                inputMode="decimal"
                step="any"
                value={pe}
                onChange={(e) => setPe(e.target.value)}
                placeholder="28.5"
                className="mt-1.5 font-mono tabular-nums"
                data-testid="pe-input"
              />
            </Field>
            <Field id="pb" label="P/B Ratio" testid="pb-input">
              <Input
                id="pb"
                type="number"
                inputMode="decimal"
                step="any"
                value={pb}
                onChange={(e) => setPb(e.target.value)}
                placeholder="4.2"
                className="mt-1.5 font-mono tabular-nums"
                data-testid="pb-input"
              />
            </Field>
          </div>

          {!canCalc && (
            <p className="text-xs text-slate-500 mt-5" data-testid="form-hint">
              Fill in Current Price, Market Cap, Dividend, P/E and P/B to
              compute the score.
            </p>
          )}
        </div>

        {/* Result */}
        <div className="lg:col-span-2 space-y-4">
          <div
            className="bg-slate-900 text-white rounded-xl shadow-sm p-6 sm:p-8"
            data-testid="score-card"
          >
            <div className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-slate-400 mb-3">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              Stock Quality Score
            </div>
            <div
              className="font-heading font-black tracking-tighter text-5xl sm:text-6xl leading-none tabular-nums"
              data-testid="final-score"
            >
              {isGuest
                ? <span className="text-2xl sm:text-3xl tracking-tight">Full members only</span>
                : finalScore === null ? "—" : fmtPct(finalScore)}
            </div>
            <div className="text-xs text-slate-400 mt-2">
              {ticker ? <span className="font-mono">{ticker}</span> : "Posterior"}
              {industry ? ` · ${industry}` : ""}
            </div>
          </div>

          {isAdmin && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-4">
              Calculation Trace
            </div>
            <div className="space-y-3 text-sm">
              <Trace
                label="Step 1 · PE update"
                formula={
                  peN !== null
                    ? `BAYES(50, ${70 - peN}, ${30 + peN})`
                    : "BAYES(50, 70−PE, 30+PE)"
                }
                value={result ? fmtPct(result.step1) : "—"}
                testid="step1-value"
              />
              <Trace
                label="Step 2 · PB update"
                formula={
                  pbN !== null && result
                    ? `BAYES(${fmt(result.step1)}, ${70 - pbN * 10}, ${30 + pbN * 10})`
                    : "BAYES(step1, 70−PB×10, 30+PB×10)"
                }
                value={result ? fmtPct(result.step2) : "—"}
                testid="step2-value"
              />
              <Trace
                label="Step 3 · Dividend / Price"
                formula={
                  dividendN !== null && priceN
                    ? `${dividendN} / ${priceN}`
                    : "dividend / price"
                }
                value={result ? fmt(result.step3, 4) : "—"}
                testid="step3-value"
              />
              <Trace
                label="Step 4 · Yield update"
                formula={
                  result
                    ? marketCapN < 1
                      ? "marketCap < 1B → 0"
                      : `BAYES(${fmt(result.step2)}, ${fmt(50 + result.step3 * 1000)}, ${fmt(50 - result.step3 * 1000)})`
                    : "MIN(99, BAYES(step2, 50+Z×1000, 50−Z×1000))"
                }
                value={result ? fmtPct(result.step4) : "—"}
                testid="step4-value"
                highlight
              />
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, suffix, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label
          htmlFor={id}
          className="text-xs tracking-[0.15em] uppercase text-slate-600"
        >
          {label}
        </Label>
        {suffix && <span className="text-xs text-slate-400">{suffix}</span>}
      </div>
      {children}
    </div>
  );
}

function Trace({ label, formula, value, testid, highlight }) {
  return (
    <div
      className={`flex items-center justify-between gap-4 ${
        highlight ? "pt-3 mt-1 border-t border-slate-100" : ""
      }`}
    >
      <div className="min-w-0">
        <div
          className={`text-xs tracking-[0.15em] uppercase ${
            highlight ? "text-slate-900 font-semibold" : "text-slate-500"
          }`}
        >
          {label}
        </div>
        <div className="text-[11px] text-slate-400 font-mono truncate mt-0.5">
          {formula}
        </div>
      </div>
      <div
        className={`font-mono tabular-nums whitespace-nowrap ${
          highlight ? "text-slate-900 font-semibold" : "text-slate-700"
        }`}
        data-testid={testid}
      >
        {value}
      </div>
    </div>
  );
}
